import {
  createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode,
} from 'react'

/**
 * App-wide modal dialogs — themed replacements for the native
 * window.alert / window.confirm / window.prompt, which the browser
 * draws with OS chrome that ignores our dark theme.
 *
 * The API is promise-based so call sites stay close to the originals:
 *   await showAlert('Saved.')
 *   if (!(await showConfirm('Delete this?'))) return
 *   const name = await showPrompt('Rename', { defaultValue: current })
 */

type DialogKind = 'alert' | 'confirm' | 'prompt'

export interface DialogOptions {
  title?: string
  confirmText?: string
  cancelText?: string
  /** Red confirm button — for destructive actions. */
  danger?: boolean
  /** prompt only — initial input value. */
  defaultValue?: string
  /** prompt only — input placeholder. */
  placeholder?: string
}

interface DialogState extends DialogOptions {
  kind: DialogKind
  message: string
}

interface DialogContextValue {
  showAlert: (message: string, opts?: DialogOptions) => Promise<void>
  showConfirm: (message: string, opts?: DialogOptions) => Promise<boolean>
  showPrompt: (message: string, opts?: DialogOptions) => Promise<string | null>
}

const noop = () => {}
const DialogContext = createContext<DialogContextValue>({
  showAlert: async () => {},
  showConfirm: async () => false,
  showPrompt: async () => null,
})

export function DialogProvider({ children }: { children: ReactNode }) {
  const [dialog, setDialog] = useState<DialogState | null>(null)
  const [inputValue, setInputValue] = useState('')
  const resolveRef = useRef<(value: unknown) => void>(noop)
  const inputRef = useRef<HTMLInputElement>(null)
  const confirmBtnRef = useRef<HTMLButtonElement>(null)

  const open = useCallback((state: DialogState): Promise<unknown> => {
    setInputValue(state.defaultValue ?? '')
    setDialog(state)
    return new Promise((resolve) => { resolveRef.current = resolve })
  }, [])

  const finish = useCallback((value: unknown) => {
    const resolve = resolveRef.current
    resolveRef.current = noop
    setDialog(null)
    resolve(value)
  }, [])

  const showAlert = useCallback(
    (message: string, opts?: DialogOptions) =>
      open({ ...opts, kind: 'alert', message }) as Promise<void>,
    [open],
  )
  const showConfirm = useCallback(
    (message: string, opts?: DialogOptions) =>
      open({ ...opts, kind: 'confirm', message }) as Promise<boolean>,
    [open],
  )
  const showPrompt = useCallback(
    (message: string, opts?: DialogOptions) =>
      open({ ...opts, kind: 'prompt', message }) as Promise<string | null>,
    [open],
  )

  // Confirm = OK / true / the typed value. Cancel = false / null.
  const confirmValue = dialog?.kind === 'prompt' ? inputValue
    : dialog?.kind === 'confirm' ? true
    : undefined
  const cancelValue = dialog?.kind === 'prompt' ? null
    : dialog?.kind === 'confirm' ? false
    : undefined

  const onConfirm = useCallback(() => finish(confirmValue), [finish, confirmValue])
  const onCancel = useCallback(() => finish(cancelValue), [finish, cancelValue])

  useEffect(() => {
    if (!dialog) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); onCancel() }
      else if (e.key === 'Enter' && dialog?.kind !== 'prompt') { e.preventDefault(); onConfirm() }
    }
    document.addEventListener('keydown', onKey)
    // Focus the input (prompt) or the confirm button once, after paint —
    // not on every render, which would steal focus mid-interaction.
    const t = window.setTimeout(() => {
      if (dialog.kind === 'prompt') inputRef.current?.focus()
      else confirmBtnRef.current?.focus()
    }, 30)
    return () => {
      document.removeEventListener('keydown', onKey)
      window.clearTimeout(t)
    }
  }, [dialog, onCancel, onConfirm])

  const isAlert = dialog?.kind === 'alert'
  const title = dialog?.title
    ?? (dialog?.kind === 'alert' ? 'Notice'
      : dialog?.kind === 'prompt' ? 'Enter a value'
      : 'Please confirm')

  return (
    <DialogContext.Provider value={{ showAlert, showConfirm, showPrompt }}>
      {children}
      {dialog && (
        <div
          onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel() }}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            style={{
              width: '100%', maxWidth: 420,
              background: 'var(--bg-card, #1c1c28)',
              border: '1px solid var(--border-default, #33334a)',
              borderRadius: 14,
              padding: 22,
              boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
              fontFamily: 'Inter, sans-serif',
              color: 'var(--text-primary, #fff)',
              animation: 'fadeIn 0.16s ease',
            }}
          >
            <div
              style={{
                fontFamily: 'Syne, Inter, sans-serif',
                fontWeight: 600, fontSize: 17, marginBottom: 8,
              }}
            >
              {title}
            </div>
            <div
              style={{
                fontSize: 14, lineHeight: 1.55,
                color: 'var(--text-secondary, #c7c7d4)',
                whiteSpace: 'pre-wrap',
              }}
            >
              {dialog.message}
            </div>

            {dialog.kind === 'prompt' && (
              <input
                ref={inputRef}
                value={inputValue}
                placeholder={dialog.placeholder || ''}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onConfirm() } }}
                style={{
                  width: '100%', marginTop: 14, boxSizing: 'border-box',
                  background: 'var(--bg-elevated, #14141e)',
                  border: '1px solid var(--border-default, #33334a)',
                  borderRadius: 8, padding: '9px 11px',
                  color: 'var(--text-primary, #fff)',
                  fontSize: 14, fontFamily: 'Inter, sans-serif', outline: 'none',
                }}
              />
            )}

            <div
              style={{
                display: 'flex', justifyContent: 'flex-end',
                gap: 8, marginTop: 20,
              }}
            >
              {!isAlert && (
                <button
                  type="button"
                  onClick={onCancel}
                  style={{
                    background: 'transparent',
                    color: 'var(--text-secondary, #c7c7d4)',
                    border: '1px solid var(--border-default, #33334a)',
                    borderRadius: 8, padding: '8px 16px',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {dialog.cancelText || 'Cancel'}
                </button>
              )}
              <button
                type="button"
                ref={confirmBtnRef}
                onClick={onConfirm}
                style={{
                  background: dialog.danger ? '#EF4444' : 'var(--accent-purple, #7C3AED)',
                  color: '#fff', border: 'none',
                  borderRadius: 8, padding: '8px 16px',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {dialog.confirmText || (isAlert ? 'OK' : dialog.kind === 'prompt' ? 'OK' : 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  )
}

export function useDialog() {
  return useContext(DialogContext)
}
